// components/HeroSection.tsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, IconButton, useTheme, useMediaQuery, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import AddDeviceDialog from '../AddDeviceDialog';
import { useChannelPath } from '../../../contexts/ChannelContext';

export interface HeroSlide {
    id: number;
    imageSrc: string;
    imageSrcMobile: string;
    imageAlt: string;
    buttonAction?: 'quote' | 'scroll-steps' | 'navigate';
    buttonTarget?: string;
}

const DEFAULT_SLIDES: HeroSlide[] = [
    { id: 1, imageSrc: '/assets/hero_b2b_banner.jpg', imageSrcMobile: '/assets/hero_b2b_banner_m1.jpg', imageAlt: '기업 맞춤 매입 프로세스' },
    { id: 2, imageSrc: '/assets/hero_b2b_banner2.jpg', imageSrcMobile: '/assets/hero_b2b_banner_m2.jpg', imageAlt: '대량 구매 및 판매 지원' },
    { id: 3, imageSrc: '/assets/hero_b2b_banner3.jpg', imageSrcMobile: '/assets/hero_b2b_banner_m3.jpg', imageAlt: '기업 전용 서비스' },
];

interface HeroSectionProps {
    slides?: HeroSlide[];
}

const HeroSection: React.FC<HeroSectionProps> = ({ slides = DEFAULT_SLIDES }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const channelPath = useChannelPath();

    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const autoPlaySpeed = slides.length > 4 ? 4000 : 2000;

    const goToSlide = (index: number) => {
        setIsTransitioning(true);
        setCurrentSlide(index);
        setTimeout(() => setIsTransitioning(false), 800);
    };

    const nextSlide = () => {
        setCurrentSlide(prev => {
            const nextIndex = (prev + 1) % slides.length;
            setIsTransitioning(true);
            setTimeout(() => setIsTransitioning(false), 800);
            return nextIndex;
        });
    };

    const prevSlide = () => {
        setCurrentSlide(prev => {
            const prevIndex = (prev - 1 + slides.length) % slides.length;
            setIsTransitioning(true);
            setTimeout(() => setIsTransitioning(false), 800);
            return prevIndex;
        });
    };

    // 슬라이드 버튼 클릭 핸들러
    const handleSlideButtonClick = useCallback((slide: HeroSlide) => {
        if (!slide.buttonAction) return;
        switch (slide.buttonAction) {
            case 'quote':
                setQuoteDialogOpen(true);
                break;
            case 'scroll-steps':
                document.getElementById('steps-section')?.scrollIntoView({ behavior: 'smooth' });
                break;
            case 'navigate':
                if (slide.buttonTarget) {
                    window.location.href = channelPath(slide.buttonTarget);
                }
                break;
        }
    }, [channelPath]);

    useEffect(() => {
        if (!isPaused) {
            intervalRef.current = setInterval(() => {
                setCurrentSlide(prev => {
                    const nextIndex = (prev + 1) % slides.length;
                    setIsTransitioning(true);
                    setTimeout(() => setIsTransitioning(false), 800);
                    return nextIndex;
                });
            }, autoPlaySpeed);
        } else {
            if (intervalRef.current) clearInterval(intervalRef.current);
        }
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [isPaused, autoPlaySpeed]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') {
                setCurrentSlide(prev => { const i = (prev - 1 + slides.length) % slides.length; setIsTransitioning(true); setTimeout(() => setIsTransitioning(false), 800); return i; });
            }
            if (e.key === 'ArrowRight') {
                setCurrentSlide(prev => { const i = (prev + 1) % slides.length; setIsTransitioning(true); setTimeout(() => setIsTransitioning(false), 800); return i; });
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.targetTouches[0].clientX);
    const handleTouchMove = (e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX);
    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        if (distance > 50) setCurrentSlide(prev => { const i = (prev + 1) % slides.length; setIsTransitioning(true); setTimeout(() => setIsTransitioning(false), 800); return i; });
        if (distance < -50) setCurrentSlide(prev => { const i = (prev - 1 + slides.length) % slides.length; setIsTransitioning(true); setTimeout(() => setIsTransitioning(false), 800); return i; });
    };

    return (
        <>
            <Box
                ref={containerRef}
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                sx={{
                    position: 'relative',
                    maxWidth: { xs: '100%', md: '50%' },
                    mx: 'auto',
                    height: { xs: '35vh', sm: '38vh', md: '33vh' },
                    overflow: 'hidden',
                    background: 'transparent',
                    borderRadius: { xs: 0, md: 3 },
                    mt: { xs: 0, md: 1 },
                    boxShadow: { xs: 'none', md: '0 8px 32px rgba(0,0,0,0.08)' },
                }}
            >
                {/* 캐러셀 트랙 */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0, left: 0, width: '100%', height: '100%',
                        display: 'flex',
                        transform: `translateX(-${currentSlide * 100}%)`,
                        transition: isTransitioning ? 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                        willChange: 'transform',
                    }}
                >
                    {slides.map((slide, index) => (
                        <Box
                            key={slide.id}
                            sx={{
                                position: 'relative',
                                width: '100%', height: '100%',
                                flexShrink: 0,
                                overflow: 'hidden',
                                opacity: Math.abs(currentSlide - index) <= 1 ? 1 : 0.3,
                                transition: 'opacity 0.8s ease-in-out',
                            }}
                        >
                            <Box sx={{
                                width: '100%', height: '100%',
                                position: 'relative', overflow: 'hidden',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: 'transparent',
                            }}>
                                <Box
                                    component="img"
                                    src={isMobile ? slide.imageSrcMobile : slide.imageSrc}
                                    alt={slide.imageAlt}
                                    sx={{
                                        width: '100%', height: '100%',
                                        objectFit: 'cover', objectPosition: 'top center',
                                    }}
                                />

                                {/* 투명 버튼 클릭 오버레이 */}
                                {slide.buttonAction && (
                                    <Box
                                        onClick={() => handleSlideButtonClick(slide)}
                                        sx={{
                                            position: 'absolute',
                                            left: '10%',
                                            bottom: '12%',
                                            width: '18%',
                                            height: '14%',
                                            cursor: 'pointer',
                                            zIndex: 5,
                                            borderRadius: 50,
                                            // 디버그 시 주석 해제: background: 'rgba(255,0,0,0.2)',
                                        }}
                                    />
                                )}

                                {/* 그라디언트 오버레이 (데스크탑) */}
                                {!isMobile && (
                                    <Box sx={{
                                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.03) 0%, transparent 40%, rgba(0,0,0,0.06) 100%)',
                                        pointerEvents: 'none',
                                    }} />
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* 인디케이터 - 번호 + 좌우 화살표 통합 */}
                <Box sx={{
                    position: 'absolute',
                    bottom: { xs: 8, sm: 12, md: 16 },
                    right: { xs: 'auto', md: 20 },
                    left: { xs: '50%', md: 'auto' },
                    transform: { xs: 'translateX(-50%)', md: 'none' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    zIndex: 10,
                    padding: { xs: '4px 8px', md: '4px 6px' },
                    background: 'rgba(0, 0, 0, 0.35)',
                    backdropFilter: 'blur(12px)',
                    borderRadius: 50,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                }}>
                    <IconButton onClick={prevSlide} size="small" sx={{ color: '#fff', width: 26, height: 26, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                        <KeyboardArrowLeft sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography sx={{
                        color: '#fff', fontSize: '0.72rem', fontWeight: 600,
                        minWidth: 36, textAlign: 'center',
                        userSelect: 'none',
                    }}>
                        {currentSlide + 1} / {slides.length}
                    </Typography>
                    <IconButton onClick={nextSlide} size="small" sx={{ color: '#fff', width: 26, height: 26, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
                        <KeyboardArrowRight sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {/* 프로그레스 바 (하단) */}
                <Box sx={{
                    position: 'absolute',
                    bottom: 0, left: 0, width: '100%', height: 3,
                    background: 'rgba(255, 255, 255, 0.1)', zIndex: 10,
                }}>
                    <Box sx={{
                        height: '100%',
                        width: `${((currentSlide + 1) / slides.length) * 100}%`,
                        background: 'linear-gradient(90deg, #3b82f6, #0ea5e9)',
                        transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 0 10px rgba(59, 130, 246, 0.5)',
                    }} />
                </Box>
            </Box>

            {/* 시세 조회 다이얼로그 */}
            <AddDeviceDialog
                open={quoteDialogOpen}
                onClose={() => setQuoteDialogOpen(false)}
                mode="quote"
            />
        </>
    );
};

export default HeroSection;
